import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES } from "@/lib/calc";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", name: "", email: "", password: "", currency: "AUD", access_code: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await register(form);
      if (res?.business?.subscription_status === "comped") { navigate("/dashboard"); return; }
      try {
        const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      } catch (billingErr) {
        navigate("/billing");
      }
    }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <form onSubmit={submit} className="w-full max-w-md space-y-4" data-testid="register-form">
        <div className="flex items-center gap-2.5 mb-2">
          <img src="/azordi-logo.webp" alt="azordi" className="h-7 w-auto" data-testid="brand-logo" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-2xl">Create your business</h2>
          <p className="text-muted-foreground text-sm mt-1">Set up in minutes and start your 14-day free trial</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Business name</label>
          <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className="h-11 rounded-lg" data-testid="register-business" required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Your name</label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-lg" data-testid="register-name" required />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Currency</label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="h-11 rounded-lg" data-testid="register-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.values(CURRENCIES).map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Email</label>
          <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11 rounded-lg" data-testid="register-email" required />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Password</label>
          <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="h-11 rounded-lg" data-testid="register-password" required minLength={6} />
        </div>
        {error && <div className="text-sm text-destructive" data-testid="register-error">{error}</div>}
        <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg" data-testid="register-submit">
          {loading ? "Creating…" : "Start free trial"}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Already have an account? <Link to="/login" className="text-primary font-medium" data-testid="link-login">Sign in</Link>
        </p>
      </form>
    </div>
  );
}
